import React from "react";
import { Typography, styled } from "@material-ui/core";

import {
  Activity,
  TicketActivity,
  BillActivity,
  CreditActivity,
  ManualBillActivity,
  NoteActivity,
  DemandActivity,
  CommunicationActivity,
  AgreementActivity,
  FINANCIAL_ACTIVITY_TYPES,
  FinancialActivity,
  getActivityTypeDisplayName,
} from "../../../api/services";
import { formatDatetime } from "../../../utils/formatDate";
import {
  CustomTableRow,
  SingleLineTableCell,
  MultiLineTableCell,
} from "../../../components/CustomTableComponents";
import { TicketActivitySummaryCell } from "./TicketActivityItem";
import { BillActivitySummaryCell } from "./BillActivityItem";
import { CreditActivitySummaryCell } from "./CreditActivityItem";
import { NoteActivitySummaryCell } from "./NoteActivityItem";
import { DemandActivitySummaryCell } from "./DemandActivityItem";
import { CommActivitySummaryCell } from "./CommActivityItem";
import { AgreementActivitySummaryCell } from "./AgreementActivityItem";
import { FinancialActivitySummaryCell } from "./FinancialActivityItem";
import { ManualBillActivitySummaryCell } from "./ManualBillActivityItem";
import { CustomTableRowProps } from "../../../components/CustomTableComponents/CustomTableRow";

const ActivitySingleLineCell = styled(SingleLineTableCell)(() => ({
  verticalAlign: "top",
}));

const ActivityMultiLineCell = styled(MultiLineTableCell)(() => ({
  verticalAlign: "top",
}));

const createActivitySummaryCell = (activity: Activity, isGrey: boolean) => {
  const { type } = activity;
  switch (type) {
    case "Ticket":
      return (
        <TicketActivitySummaryCell
          activity={activity as TicketActivity}
          isGrey={isGrey}
        />
      );
    case "Bill":
      return (
        <BillActivitySummaryCell
          activity={activity as BillActivity}
          isGrey={isGrey}
        />
      );
    case "Credit":
      return (
        <CreditActivitySummaryCell
          activity={activity as CreditActivity}
          isGrey={isGrey}
        />
      );
    case "ManualBill":
      return (
        <ManualBillActivitySummaryCell
          activity={activity as ManualBillActivity}
          isGrey={isGrey}
        />
      );
    case "Note":
      return (
        <NoteActivitySummaryCell
          activity={activity as NoteActivity}
          isGrey={isGrey}
        />
      );
    case "WrapUpNote":
      return (
        <DemandActivitySummaryCell
          activity={activity as DemandActivity}
          isGrey={isGrey}
        />
      );
    case "Communication":
      return (
        <CommActivitySummaryCell
          activity={activity as CommunicationActivity}
          isGrey={isGrey}
        />
      );
    case "Agreement":
      return (
        <AgreementActivitySummaryCell
          activity={activity as AgreementActivity}
          isGrey={isGrey}
        />
      );
    default:
      if (FINANCIAL_ACTIVITY_TYPES.includes(type)) {
        return (
          <FinancialActivitySummaryCell
            activity={activity as FinancialActivity}
            isGrey={isGrey}
          />
        );
      }
      return null;
  }
};

export const ActivityRow: React.FC<
  {
    activity: Activity;
  } & CustomTableRowProps
> = ({ activity, isGrey, ...props }) => {
  const { activityDttm, type, user } = activity;
  return (
    <CustomTableRow data-testid="ActivityRow" isGrey={isGrey} {...props}>
      <ActivityMultiLineCell data-testid="ActivityRow__Cell--Summary">
        {createActivitySummaryCell(activity, isGrey || false)}
      </ActivityMultiLineCell>
      <ActivityMultiLineCell data-testid="ActivityRow__Cell--Type">
        <Typography variant="body1" component="span">
          {getActivityTypeDisplayName(type)}
        </Typography>
      </ActivityMultiLineCell>
      <ActivitySingleLineCell data-testid="ActivityRow__Cell--User">
        <Typography variant="body1" component="span">
          {user || "-"}
        </Typography>
      </ActivitySingleLineCell>
      <ActivitySingleLineCell data-testid="ActivityRow__Cell--Date">
        <Typography variant="body1" component="span">
          {formatDatetime(activityDttm)}
        </Typography>
      </ActivitySingleLineCell>
    </CustomTableRow>
  );
};
